import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowLeft, Play, Users, Clock3 } from "lucide-react";

export default function TournamentGameModeSelect() {
  const { tournamentId, gameId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const routeState = location.state;

  const [teams, setTeams] = useState([]);
  const [existingGame, setExistingGame] = useState(null);
  const [homeTeamId, setHomeTeamId] = useState(routeState?.homeTeamId || "");
  const [awayTeamId, setAwayTeamId] = useState(routeState?.awayTeamId || "");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedMode, setSelectedMode] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    const load = async () => {
      try {
        if (gameId) {
          const response = await api.get(`/tournaments/${tournamentId}/games/${gameId}`);
          if (!alive) return;
          setExistingGame(response.data);
          setHomeTeamId(response.data.home_team.id);
          setAwayTeamId(response.data.away_team.id);
        } else {
          const response = await api.get(`/tournaments/${tournamentId}`);
          if (!alive) return;
          setTeams(response.data.teams || []);
        }
      } catch (error) {
        if (!alive) return;
        console.error("[TournamentGameModeSelect] load failed", error);
        toast.error(error?.response?.data?.detail || "Unable to load game setup data");
      } finally {
        if (alive) setLoading(false);
      }
    };

    load();
    return () => {
      alive = false;
    };
  }, [gameId, tournamentId]);

  const homeTeam = useMemo(() => {
    if (existingGame?.home_team) return existingGame.home_team;
    return teams.find((team) => team.id === homeTeamId) || null;
  }, [existingGame, teams, homeTeamId]);

  const awayTeam = useMemo(() => {
    if (existingGame?.away_team) return existingGame.away_team;
    return teams.find((team) => team.id === awayTeamId) || null;
  }, [existingGame, teams, awayTeamId]);

  const rosterPreview = useMemo(() => {
    const rows = [];
    for (const team of [homeTeam, awayTeam]) {
      if (!team) continue;
      (team.players || []).forEach((player) => {
        rows.push({
          id: player.id,
          teamId: player.team_id || (team.id || ""),
          teamName: team.name,
          name: player.name || "Unnamed",
          email: player.email || "",
          phone: player.phone || "",
          position: player.position || "",
          location: player.location || "",
        });
      });
    }
    return rows;
  }, [homeTeam, awayTeam]);

  const createOrReuseGame = async () => {
    if (gameId) return gameId;
    const payload = { home_team_id: homeTeamId, away_team_id: awayTeamId };
    const response = await api.post(`/tournaments/${tournamentId}/games`, payload);
    return response.data.id;
  };

  const openMode = async (mode, liveOption = null) => {
    if (!homeTeamId || !awayTeamId) {
      toast.error("Select both teams before continuing");
      return;
    }
    if (homeTeamId === awayTeamId) {
      toast.error("Choose two different teams");
      return;
    }
    if (!user) {
      toast.error("Sign in to continue.");
      return;
    }

    setSaving(true);
    try {
      const targetGameId = await createOrReuseGame();
      if (mode === "live") {
        if (liveOption === "single") {
          navigate(`/tournaments/${tournamentId}/games/live/${targetGameId}`, {
            state: { homeTeamId, awayTeamId },
          });
          return;
        }
        if (liveOption === "two") {
          navigate(`/tournaments/${tournamentId}/games/two-scorer`, {
            state: { homeTeamId, awayTeamId, gameId: targetGameId },
          });
          return;
        }

        setSelectedMode("live");
        return;
      }

      navigate(`/tournaments/${tournamentId}/games/${targetGameId}`);
    } catch (error) {
      console.error("[TournamentGameModeSelect] create game failed", error);
      toast.error(error?.response?.data?.detail || "Unable to start game");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="max-w-6xl mx-auto px-6 py-12 text-zinc-400">Loading match setup…</div>;
  }

  const hasTeams = homeTeam && awayTeam;
  const noSelection = !gameId && !routeState?.homeTeamId && !routeState?.awayTeamId;

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 sm:px-6">
      <div className="flex items-center justify-between gap-4 mb-8">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-400 hover:text-white">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-court">Match Setup</div>
      </div>

      <div className="border border-zinc-800 p-8 bg-zinc-950/50 space-y-6">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.3em] text-court">{gameId ? "Open Scheduled Game" : "New Game Setup"}</div>
          <h1 className="font-display font-black text-4xl uppercase mt-3">Choose a recording mode</h1>
          <p className="text-sm text-zinc-400 mt-3">
            {gameId
              ? "This scheduled game is ready to begin. Choose live scoring to track each action or manual entry to record final results after the match."
              : "Pick the game mode before the match starts. Live scoring is best for real-time tracking, while manual entry is ideal for completed games."
            }
          </p>
        </div>

        {!hasTeams && (
          <div className="rounded-2xl border border-zinc-800 bg-zinc-900/80 p-6 text-sm text-zinc-300">
            <div className="font-semibold text-white mb-2">Select both teams to continue.</div>
            {gameId ? (
              <p>If this scheduled game is missing team details, go back and choose a valid game from your tournament list.</p>
            ) : (
              <p>Return to the tournament games page and select home and away teams before choosing a game mode.</p>
            )}
            <div className="mt-4">
              <Link to={`/tournaments/${tournamentId}/games`} className="inline-flex items-center gap-2 rounded-md border border-zinc-700 bg-zinc-950 px-4 py-3 text-sm text-white hover:border-court">
                <Users className="w-4 h-4" /> Back to games
              </Link>
            </div>
          </div>
        )}

        {noSelection && !gameId && (
          <div className="rounded-2xl border border-yellow-700 bg-yellow-950/20 p-6 text-sm text-yellow-200">
            <div className="font-semibold text-yellow-100">Start by picking teams from the tournament games page.</div>
            <p className="mt-2">Use the game creation panel to select the home and away teams, then continue to this screen.</p>
          </div>
        )}

        {hasTeams && (
          <div className="grid gap-6 lg:grid-cols-[1.4fr_0.8fr]">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {[homeTeam, awayTeam].map((team, idx) => (
                  <div key={team?.id || idx} className="border border-zinc-800 bg-zinc-950 p-5 rounded-2xl">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">{idx === 0 ? "Home Team" : "Away Team"}</div>
                    <div className="font-display font-black text-2xl uppercase mt-3">{team?.name || "Unknown"}</div>
                    <div className="text-sm text-zinc-400 mt-2">{(team?.players?.length || 0)} players preloaded</div>
                  </div>
                ))}
              </div>

              <div className="border border-court/30 bg-court/5 rounded-2xl p-5">
                <div className="text-[11px] uppercase tracking-[0.3em] text-court font-bold">Match Options</div>
                <div className="mt-3 grid gap-3">
                  <button
                    onClick={() => openMode("live")}
                    disabled={saving}
                    className="w-full rounded-2xl border border-court bg-court/10 px-5 py-5 text-left hover:border-white transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xl font-bold text-white">Live Scoring</div>
                        <p className="text-sm text-zinc-400 mt-2">Track the full game action in real time and switch players as needed.</p>
                      </div>
                      <Play className="w-5 h-5 text-court" />
                    </div>
                  </button>

                  <button
                    onClick={() => openMode("manual")}
                    disabled={saving}
                    className="w-full rounded-2xl border border-zinc-800 bg-zinc-950 px-5 py-5 text-left hover:border-court transition-colors"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-xl font-bold text-white">Manual Final Score</div>
                        <p className="text-sm text-zinc-400 mt-2">Enter the final score and player stats once the game is complete.</p>
                      </div>
                      <Clock3 className="w-5 h-5 text-zinc-400" />
                    </div>
                  </button>
                </div>
              </div>

              {selectedMode === "live" && (
                <div className="border border-zinc-800 bg-zinc-950 rounded-3xl p-6 mt-6">
                  <div className="text-[11px] uppercase tracking-[0.3em] text-court font-bold">Choose Live Flow</div>
                  <div className="mt-4 grid gap-3">
                    <button
                      onClick={() => openMode("live", "single")}
                      disabled={saving}
                      className="w-full rounded-2xl border border-court bg-court/10 px-5 py-5 text-left hover:border-white transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-lg font-bold text-white">Single Scorer</div>
                          <p className="text-sm text-zinc-400 mt-2">Open the live tracking screen for one scorer to manage the whole game.</p>
                        </div>
                        <Play className="w-5 h-5 text-court" />
                      </div>
                    </button>

                    <button
                      onClick={() => openMode("live", "two")}
                      disabled={saving}
                      className="w-full rounded-2xl border border-zinc-800 bg-zinc-950 px-5 py-5 text-left hover:border-court transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div>
                          <div className="text-lg font-bold text-white">Two Scorer</div>
                          <p className="text-sm text-zinc-400 mt-2">Use the upcoming two-scorer workflow for split scoring collaboration.</p>
                        </div>
                        <Clock3 className="w-5 h-5 text-zinc-400" />
                      </div>
                    </button>
                  </div>

                  <button
                    onClick={() => setSelectedMode(null)}
                    className="mt-4 w-full h-12 rounded-2xl border border-zinc-800 text-zinc-400 hover:text-white uppercase tracking-[0.25em]"
                  >
                    Change mode
                  </button>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-800 bg-zinc-950 p-6">
              <div className="text-[11px] uppercase tracking-[0.3em] text-court font-bold">Roster Preview</div>
              <div className="mt-4 space-y-3">
                {rosterPreview.map((player) => (
                  <div key={`${player.teamId}-${player.id}`} className="rounded-2xl border border-zinc-800 bg-zinc-900 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-medium text-white">{player.name}</div>
                        <div className="text-xs text-zinc-500 mt-1">{player.teamName}</div>
                      </div>
                      <div className="text-xs uppercase text-zinc-400 tracking-[0.2em]">{player.position || "Player"}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
